import { HealthCheck } from "../../types";

export interface BodyMetric {
  key: string;
  label: string;
  unit: string;
  min: number;
  max: number;
  step: string;
  low?: number;
  high?: number;
}

export const BODY_METRICS: BodyMetric[] = [
  {
    key: "weight",
    label: "Berat",
    unit: "kg",
    min: 40.16,
    max: 54.38,
    step: "0.1",
    low: 0.1,
    high: 0.14,
  },
  {
    key: "body_fat",
    label: "Lemak",
    unit: "%",
    min: 18,
    max: 28,
    step: "0.1",
    low: 0.3,
    high: 0.18,
  },
  {
    key: "body_mineral",
    label: "Mineral",
    unit: "%",
    min: 3.6,
    max: 4.4,
    step: "0.1",
    low: 0.1,
    high: 0.1,
  },
  {
    key: "body_protein",
    label: "Protein",
    unit: "%",
    min: 13,
    max: 17,
    step: "0.1",
    low: 0.02,
    high: 0.25,
  },
  {
    key: "body_water",
    label: "Air",
    unit: "%",
    min: 48.8,
    max: 62.1,
    step: "0.1",
    low: 0.03,
    high: 0.25,
  },
  {
    key: "muscle_percentage",
    label: "Persentase Otot",
    unit: "%",
    min: 65,
    max: 81.9,
    step: "0.1",
    low: 0.22,
    high: 0.2,
  },
  {
    key: "skeletal_muscle",
    label: "Otot Rangka",
    unit: "%",
    min: 35.8,
    max: 46.2,
    step: "0.1",
    low: 0.2,
    high: 0.2,
  },
];

export type Assessment =
  | "normal"
  | "sedikit rendah"
  | "sedikit tinggi"
  | "kurang"
  | "tinggi";

export const assess = (
  value: number,
  min: number,
  max: number,
  lowFactor?: number,
  highFactor?: number,
): Assessment => {
  const lowThreshold = min * (1 - (lowFactor ?? 0.1));
  const highThreshold = max * (1 + (highFactor ?? 0.1));
  if (value < min) {
    return value < lowThreshold ? "kurang" : "sedikit rendah";
  }
  if (value > max) {
    return value > highThreshold ? "tinggi" : "sedikit tinggi";
  }
  return "normal";
};

export const ASSESSMENT_STYLE: Record<Assessment, string> = {
  normal: "bg-green-100 text-green-700",
  "sedikit rendah": "bg-amber-100 text-amber-700",
  "sedikit tinggi": "bg-amber-100 text-amber-700",
  kurang: "bg-red-100 text-red-700",
  tinggi: "bg-red-100 text-red-700",
};

export const getBodyValue = (
  check: HealthCheck,
  key: string,
): number | null => {
  const v = (check as unknown as Record<string, unknown>)[key];
  if (v === null || v === undefined || v === "") return null;
  return Number(v);
};

export interface PersonProfile {
  gender: "male" | "female" | null;
  age: number | null;
  height: number | null;
}

interface GenderRange {
  male: { min: number; max: number };
  female: { min: number; max: number };
}

const genderRange = (
  profile: PersonProfile,
  table: GenderRange,
): { min: number; max: number } | null => {
  if (!profile.gender) return null;
  return table[profile.gender];
};

type AgeGroup = "teen" | "adult" | "middle" | "senior";

export const getAgeGroup = (age: number | null): AgeGroup => {
  if (age === null) return "adult";

  if (age < 18) return "teen";
  if (age < 40) return "adult";
  if (age < 60) return "middle";

  return "senior";
};

export const getWeightRange = (
  profile: PersonProfile,
): { min: number; max: number } | null => {
  if (!profile.height || profile.height <= 0) {
    return null;
  }

  const h = profile.height / 100;

  const isMale =
    profile.gender?.toLowerCase() === "male" ||
    profile.gender?.toLowerCase() === "laki-laki" ||
    profile.gender?.toLowerCase() === "laki laki" ||
    profile.gender?.toLowerCase() === "pria";

  if (isMale) {
    return {
      min: Math.round(18.7 * h * h * 100) / 100,
      max: Math.round(25.3 * h * h * 100) / 100,
    };
  }

  // Perempuan
  return {
    min: Math.round(17.85 * h * h * 100) / 100,
    max: Math.round(24.17 * h * h * 100) / 100,
  };
};

export const getMineralRange = (
  profile: PersonProfile,
  weight: number | null,
): { min: number; max: number } | null => {
  if (
    !profile.height ||
    profile.height <= 0 ||
    !profile.age ||
    profile.age < 18 ||
    !weight ||
    weight <= 0
  ) {
    return null;
  }

  const heightM = profile.height / 100;
  const bmi = weight / (heightM * heightM);

  const gender = profile.gender?.toLowerCase();

  const isMale =
    gender === "male" ||
    gender === "laki-laki" ||
    gender === "laki laki" ||
    gender === "pria";

  // =====================================================
  // LAKI-LAKI
  // =====================================================
  if (isMale) {
    const points = [
      // BMI sangat rendah
      { bmi: 17.65, min: 6.5, max: 7.9 },

      // BMI normal
      { bmi: 21.26, min: 5.4, max: 6.6 },

      // BMI normal-tinggi
      { bmi: 23.22, min: 4.9, max: 6.0 },

      // BMI overweight
      { bmi: 27.29, min: 4.2, max: 5.1 },

      // BMI tinggi
      { bmi: 28.45, min: 4.0, max: 4.9 },
    ];

    // Di bawah titik BMI terendah
    if (bmi <= points[0].bmi) {
      return {
        min: points[0].min,
        max: points[0].max,
      };
    }

    // Di atas titik BMI tertinggi
    if (bmi >= points[points.length - 1].bmi) {
      return {
        min: points[points.length - 1].min,
        max: points[points.length - 1].max,
      };
    }

    // Interpolasi
    for (let i = 0; i < points.length - 1; i++) {
      const current = points[i];
      const next = points[i + 1];

      if (bmi >= current.bmi && bmi <= next.bmi) {
        const ratio = (bmi - current.bmi) / (next.bmi - current.bmi);

        const min = current.min + (next.min - current.min) * ratio;

        const max = current.max + (next.max - current.max) * ratio;

        return {
          min: Math.round(min * 10) / 10,
          max: Math.round(max * 10) / 10,
        };
      }
    }

    return null;
  }

  // =====================================================
  // PEREMPUAN
  // =====================================================

  const points = [
    { bmi: 19.6, min: 5.0, max: 6.2 },
    { bmi: 23.3, min: 4.2, max: 5.2 },
    { bmi: 27.0, min: 3.6, max: 4.4 },
    { bmi: 32.25, min: 3.1, max: 3.7 },
  ];

  if (bmi <= points[0].bmi) {
    return {
      min: points[0].min,
      max: points[0].max,
    };
  }

  if (bmi >= points[points.length - 1].bmi) {
    return {
      min: points[points.length - 1].min,
      max: points[points.length - 1].max,
    };
  }

  for (let i = 0; i < points.length - 1; i++) {
    const current = points[i];
    const next = points[i + 1];

    if (bmi >= current.bmi && bmi <= next.bmi) {
      const ratio = (bmi - current.bmi) / (next.bmi - current.bmi);

      const min = current.min + (next.min - current.min) * ratio;

      const max = current.max + (next.max - current.max) * ratio;

      return {
        min: Math.round(min * 10) / 10,
        max: Math.round(max * 10) / 10,
      };
    }
  }

  return null;
};

const BODY_FAT_RANGES: GenderRange = {
  male: { min: 10, max: 20 },
  female: { min: 18, max: 28 },
};

const BODY_PROTEIN_RANGES: GenderRange = {
  male: { min: 14, max: 18.3 },
  female: { min: 13, max: 17 },
};

const BODY_WATER_RANGES: GenderRange = {
  male: { min: 54.1, max: 68.6 },
  female: { min: 48.8, max: 62.1 },
};

const MUSCLE_PERCENT_RANGES: GenderRange = {
  male: { min: 70.2, max: 86 },
  female: { min: 65, max: 81.9 },
};

const SKELETAL_MUSCLE_RANGES: GenderRange = {
  male: { min: 40.7, max: 52.3 },
  female: { min: 35.8, max: 46.2 },
};

// Standar massa otot rangka (SMI, kg per meter kuadrat) berdasarkan gender.
const SKELETAL_MASS_SMI: GenderRange = {
  male: { min: 9.35, max: 11.44 },
  female: { min: 7.93, max: 9.69 },
};

export const getSkeletalMassRangeKg = (
  profile: PersonProfile,
): { min: number; max: number } | null => {
  if (!profile.gender || !profile.height || profile.height <= 0) {
    return null;
  }

  const g = profile.gender.toLowerCase();
  const isMale =
    g === "male" || g === "laki-laki" || g === "laki laki" || g === "pria";

  const h = profile.height / 100;

  const smi = isMale ? SKELETAL_MASS_SMI.male : SKELETAL_MASS_SMI.female;

  return {
    min: Math.round(smi.min * h * h * 10) / 10,
    max: Math.round(smi.max * h * h * 10) / 10,
  };
};

export const getMetricRange = (
  profile: PersonProfile,
  key: string,
  weight: number | null = null,
): { min: number; max: number } | null => {
  switch (key) {
    case "weight":
      return getWeightRange(profile);
    case "body_fat":
      return genderRange(profile, BODY_FAT_RANGES);
    case "body_protein":
      return genderRange(profile, BODY_PROTEIN_RANGES);
    case "body_water":
      return genderRange(profile, BODY_WATER_RANGES);
    case "body_mineral":
      return getMineralRange(profile, weight);
    case "muscle_percentage":
      return genderRange(profile, MUSCLE_PERCENT_RANGES);
    case "skeletal_muscle":
      return genderRange(profile, SKELETAL_MUSCLE_RANGES);
    default: {
      const m = BODY_METRICS.find((b) => b.key === key);
      return m ? { min: m.min, max: m.max } : null;
    }
  }
};
